import { BreakpointObserver } from '@angular/cdk/layout';

import { HttpClient } from '@angular/common/http';
import { AfterViewInit, ChangeDetectionStrategy, Component, DOCUMENT, Inject, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { MatDrawerMode, MatSidenav } from '@angular/material/sidenav';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { SidenavService } from '@services/sidenav.service';
import { LocalStorageService, SessionStorageService } from '@services/storage.service';
import { IAccount, UserService } from '@services/user.service';
import { registerAssets } from '@shared/helpers';
import { MenuItemEvent } from '@shared/menuItemEvent';
import { ServiceItem } from '@shared/serviceitem';
import { AccountScopedRouteFallbackService } from '@shared/services/account-scoped-route-fallback.service';
import { ForceRouteReloadService } from '@shared/services/force-route-reload.service';
import { UserPermissionsService } from '@shared/services/userpermissions.service';
import { SidebarItem } from '@shared/sidebaritem';
import { filter, pairwise, Subject, take, takeUntil } from 'rxjs';
import { AuthService } from './auth/auth.service';
import { serviceItems } from './topbar/servicemenu';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
	private destroyNotifier$: Subject<void> = new Subject<void>();
	public title: string = '';
	public theme: string = 'my-light-theme';
	public isExpanded: boolean = true;
	public isAdminSelected: boolean;
	public isReportSelected: boolean;
	public isLoggedIn: boolean;
	public leftSidebarMenuItemsFromChild: SidebarItem[];
	public userEmail: string;
	public permissions: any[] = [];
	public selectedService: string = '';
	public rightIsExpanded: boolean = true;
	public rightSideNavDisplayMode: MatDrawerMode = 'side';
	public accountsForTopbar = [];
	services: ServiceItem[] = serviceItems;
	userAccount = '';
	public isTerminal = false;

	@ViewChild('leftSideNav')
	leftSideNav: MatSidenav;

	rightSideNav: MatSidenav;
	@ViewChild('rightSideNav', { static: true })
	set nav(nav: MatSidenav) {
		this.rightSideNav = nav;
	}

	get nav(): MatSidenav {
		return this.rightSideNav;
	}

	rightSideNavWidgetContent: ViewContainerRef;
	@ViewChild('rightSideNavWidgetContent', {
		static: true,
		read: ViewContainerRef,
	})
	set containerRef(containerRef: ViewContainerRef) {
		console.log('got containerRef', containerRef);
		this.rightSideNavWidgetContent = containerRef;
	}

	get containerRef(): ViewContainerRef {
		return this.rightSideNavWidgetContent;
	}

	selectItem(menuItemEvent: MenuItemEvent, item: SidebarItem) {
		if (this.isExpanded === false && item.children.length) {
			this.isExpanded = true;
			this.localStorageService.set('User.Selected.SideNav.IsExpanded', this.isExpanded);
		}
	}

	constructor(
		@Inject(DOCUMENT) private document: any,
		private matIconRegistry: MatIconRegistry,
		private domSanitzer: DomSanitizer,
		private route: ActivatedRoute,
		private breakpointObserver: BreakpointObserver,
		public userService: UserService,
		public sidenavService: SidenavService,
		private sessionStorageService: SessionStorageService,
		private localStorageService: LocalStorageService,
		public authService: AuthService,
		private translateService: TranslateService,
		private router: Router,
		private userPermissions: UserPermissionsService,
		private http: HttpClient,
		private forceRouteReloadService: ForceRouteReloadService,
		private accountScopedRouteFallbackService: AccountScopedRouteFallbackService,
	) {
		// Translator/language initialization
		translateService.addLangs(['da', 'en']);
		let browserLang = translateService.getBrowserLang();
		this.sessionStorageService.set('User.PreferredLanguage', browserLang);
		let userPreferredLanguage = sessionStorageService.get('User.PreferredLanguage');
		const path = location.href.split('/').slice(3).join('/');
		const isNotCore = !path.startsWith('core');
		const isNotSignin = !path.startsWith('signin');
		const isNotAdsignin = !path.startsWith('adsignin');

		if (path.length > 2 && isNotCore && isNotSignin && isNotAdsignin) {
			this.localStorageService.set('User.DeepLink', path);
		} else if (isNotSignin && isNotAdsignin) {
			this.localStorageService.remove('User.DeepLink');
		}

		if (userPreferredLanguage) {
			browserLang = userPreferredLanguage;
		} else {
			this.sessionStorageService.set('User.PreferredLanguage', browserLang);
			userPreferredLanguage = browserLang;
		}

		translateService.use(translateService.getLangs().includes(browserLang) ? browserLang : 'en'); // set active language
		translateService.setDefaultLang(userPreferredLanguage); // used for fallback translations - set last to avoid flash of English text

		// Moved icon/SVG registration to helper class to make it a bit easier to get an overview of AppComponent
		registerAssets(this.matIconRegistry, this.domSanitzer);

		router.events.subscribe(() => {
			if (location.href.endsWith('/terminal') || location.href.includes('/sales/configurator') || location.href.includes('/mes/terminals/') || location.href.endsWith('/sales/configuration') || (location.href.endsWith('/core') && this.localStorageService.get('User.DeepLink'))) {
				this.isTerminal = true;
			} else {
				this.isTerminal = false;
			}
		});
	}

	ngOnInit(): void {
		this.authService.isLoggedIn$.pipe(take(3)).subscribe((isloggedin) => {
			const inviteToken = this.sessionStorageService.get('User.InviteToken');

			this.isLoggedIn = isloggedin && !inviteToken;
			if (isloggedin) {
				this.userService.fetchAccountsObs().subscribe({
					next: (ua) => {
						if (!this.isLoggedIn) {
							return;
						}

						const callMapAndContinue = () => {
							this.authService.mapAccounts(ua);
							this.authService.setUserPermissions(ua);
							this.userService.accounts$.pipe(filter((accs) => accs.length > 0)).subscribe((accounts) => {
								this.accountsForTopbar = accounts;
								this.authService.setIAMUser(ua, accounts, this.services);
							});
						};

						// If Caller.IP already set, proceed. Otherwise fetch public IP and set it.
						const existingIp = this.sessionStorageService.get('Caller.IP');
						if (existingIp) {
							callMapAndContinue();
						} else {
							this.http
								.get<{ ip: string }>(environment.IpLookupUri)
								.pipe(take(1))
								.subscribe({
									next: (response) => {
										this.sessionStorageService.set('Caller.IP', response.ip);
										callMapAndContinue();
									},
									error: () => {
										// On failure, proceed without caller IP
										callMapAndContinue();
									},
								});
						}
					},
					error: (error) => {
						this.authService.setUserPermissions(null);
						this.authService.setIAMUser(null, [], []);
						console.log(JSON.stringify(error));
					},
				});
			}
		});

		this.sessionStorageService.watch('User.Account').subscribe((account) => {
			const acc: IAccount = JSON.parse(account);
			if (acc != null) {
				this.userAccount = `${acc.organization.name} > ${acc.name}`;
			}
			this.userService.account = acc;
		});

		this.sidenavService.rightSidebarExpandedStatus$.pipe(takeUntil(this.destroyNotifier$)).subscribe((data) => {
			this.rightIsExpanded = data;
		});

		this.sidenavService
			.getMenuData()
			.pipe(takeUntil(this.destroyNotifier$))
			.subscribe((menuData) => {
				// Get sidebar items from "child" (embedded) component - the menu-items are coming from the menu.ts file in that component
				setTimeout(() => (this.leftSidebarMenuItemsFromChild = menuData), 0);
			});

		this.sessionStorageService.watch('User.Permissions').subscribe((val) => {
			this.permissions = JSON.parse(val);
			this.userPermissions.permissions = this.permissions;
		});

		this.sessionStorageService.watch('User.Email').subscribe((email) => {
			this.userEmail = email;
		});

		this.sessionStorageService.watch('Selected.ServiceName').subscribe((serviceName) => {
			this.selectedService = serviceName;
		});

		this.authService.isLoggedIn$.pipe(filter((s) => s === true)).subscribe(() => {});

		// Switching the active account, or the impersonated user within it, changes what data every
		// page is allowed to see - but pages only fetch their data once in ngOnInit. Reacting here,
		// centrally, means individual pages never need their own account$/impersonateUser$ handling.
		this.userService.accountChanged$.pipe(takeUntil(this.destroyNotifier$)).subscribe(() => this.reloadCurrentRoute());

		this.userService.impersonateUser$
			.pipe(
				pairwise(),
				filter(([prev, curr]) => (prev?.id ?? null) !== (curr?.id ?? null)),
				takeUntil(this.destroyNotifier$),
			)
			.subscribe(() => this.reloadCurrentRoute());
	}

	// Forces Angular to destroy and recreate the current route's components (rerunning ngOnInit, so
	// the page refetches its data) without a full browser reload. ForceReloadRouteReuseStrategy
	// (registered in AppRoutingModule) checks forceRouteReloadService to temporarily stop reusing
	// the current route's components for this one navigation; onSameUrlNavigation: 'reload' (also
	// configured there) is what makes the Router process a same-URL navigation at all.
	//
	// Some routes carry an entity id scoped to the account that was active when navigated to (e.g. a
	// specific production plan or terminal) - those redirect to a list page instead (see AccountScopedRouteFallbackService).
	private reloadCurrentRoute(): void {
		const fallback = this.accountScopedRouteFallbackService.getFallback(this.getCurrentRouteTemplate());
		const targetUrl = fallback ? `/${fallback}` : this.router.url;

		this.forceRouteReloadService.beginForceReload();
		void this.router.navigateByUrl(targetUrl).finally(() => {
			this.forceRouteReloadService.endForceReload();
		});
	}

	private getCurrentRouteTemplate(): string {
		const segments: string[] = [];
		let route = this.router.routerState.snapshot.root;
		while (route) {
			if (route.routeConfig?.path) {
				segments.push(route.routeConfig.path);
			}
			route = route.firstChild;
		}
		return segments.join('/');
	}

	ngAfterViewInit() {
		this.sidenavService.setPanel(this.rightSideNav);
		this.sidenavService.setContentVcf(this.rightSideNavWidgetContent);
		const isExpanded = this.localStorageService.get('User.Selected.SideNav.IsExpanded');
		this.isExpanded = isExpanded === 'true';
		const rightIsExpanded = this.localStorageService.get('User.Selected.SideNav.RightIsExpanded');
		this.sidenavService.setRightPanelExpansion(rightIsExpanded !== 'false');
		setTimeout(() => {}, 1000);
	}

	ngOnDestroy() {
		this.destroyNotifier$.next();
		this.destroyNotifier$.complete();
	}

	setLang(lang: string) {
		if (lang === 'reset') {
			lang = this.translateService.getBrowserLang();
		}
		this.sessionStorageService.set('User.PreferredLanguage', lang);
		this.translateService.use(lang);
	}

	toggleSideBarExpanded() {
		this.isExpanded = !this.isExpanded;
		this.localStorageService.set('User.Selected.SideNav.IsExpanded', this.isExpanded);
	}

	getExpandedStatusOnPageLoad(item: SidebarItem) {
		return typeof item.children !== 'undefined' && item.children.filter((i) => this.router.url.includes(i.path)).length === 1;
	}
}
